import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react'
import { GrowthbookFlagsEnum } from 'growthbook/types'
import { pageRoute, type PageRouteValue } from 'shared/constants/routes.constants'
import { AdminRolesEnum } from 'shared/enums/user-roles-enum'
import useUser from 'shared/hooks/use-user'
import { isSIO } from 'shared/utils/is-sio'

export type PagePermissions = Partial<Record<PageRouteValue, boolean>>

interface UsePermissionResult {
  isReady: boolean
  pagePermissions: PagePermissions
}

export const usePermission = (): UsePermissionResult => {
  const growthbook = useGrowthBook()
  const {
    checkAdminRoles,
    checkImpersonatorAdminRoles,
    isPaid,
    isWorkspaceOwner,
    isSupportImpersonation,
    isSubAccountImpersonation,
    isWsAssistant,
    isCustomer,
    isAffiliate,
    isStudent,
    isWsAdmin,
    isRootAdmin,
    isLoading,
  } = useUser()

  const whiteLabelFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.WhiteLabel)
  const isReady = !isLoading && growthbook.ready

  const isSupportOrAdmin = checkAdminRoles([
    AdminRolesEnum.support,
    AdminRolesEnum.admin,
    AdminRolesEnum.superAdmin,
  ])

  const isAdmin = checkAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin])

  const adminTemplatesPermission = checkAdminRoles([
    AdminRolesEnum.contentManager,
    AdminRolesEnum.admin,
    AdminRolesEnum.superAdmin,
  ])

  const adminCustomersPermission = checkAdminRoles([
    AdminRolesEnum.mailingManager,
    AdminRolesEnum.admin,
    AdminRolesEnum.superAdmin,
    AdminRolesEnum.support,
  ])

  const agencyPermission =
    whiteLabelFeatureIsOn &&
    !isSubAccountImpersonation &&
    isCustomer &&
    isWorkspaceOwner &&
    !isRootAdmin &&
    isSIO()

  const agencyUsersPermission =
    whiteLabelFeatureIsOn &&
    !isSubAccountImpersonation &&
    isCustomer &&
    (isWorkspaceOwner || isWsAdmin || isWsAssistant) &&
    !isRootAdmin &&
    isSIO()

  const changePasswordPermission = (() => {
    if (isSubAccountImpersonation) return false
    if (isSupportImpersonation) {
      return checkImpersonatorAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin])
    }

    return isWorkspaceOwner
  })()

  const pagePermissions: PagePermissions = {
    [pageRoute.Dashboard]: (isWorkspaceOwner || isWsAdmin) && isCustomer,
    [pageRoute.AffiliateDashboard]: isWorkspaceOwner && isAffiliate,
    [pageRoute.Subaccounts]:
      isCustomer && isWorkspaceOwner && !isSubAccountImpersonation && isSIO(),
    [pageRoute.AgencyUsers]: agencyUsersPermission,
    [pageRoute.AgencySettings]: agencyPermission,
    [pageRoute.AgencyPricePlans]: agencyPermission,
    [pageRoute.AgencyPricePlanDetails]: agencyPermission && isPaid,
    [pageRoute.Contacts]: isCustomer,
    [pageRoute.AssistantContacts]: isCustomer,
    [pageRoute.ContactsBin]: isCustomer,
    [pageRoute.ContactsCreate]: isCustomer,
    [pageRoute.ContactsImport]: isCustomer,
    [pageRoute.ContactDetails]: isCustomer,
    [pageRoute.ContactEmailHistory]: isCustomer,
    [pageRoute.Tags]: isCustomer,
    [pageRoute.CrmPipeline]: isCustomer,
    [pageRoute.CrmPipelineDetails]: isCustomer,
    [pageRoute.Calendar]: isCustomer,
    [pageRoute.CalendarAvailability]: isCustomer,
    [pageRoute.CalendarBookings]: isCustomer,
    [pageRoute.CalendarCreate]: isCustomer,
    [pageRoute.CalendarEventDetails]: isCustomer,
    [pageRoute.Websites]: isCustomer,
    [pageRoute.Funnels]: isCustomer,
    [pageRoute.FunnelDetails]: isCustomer,
    [pageRoute.Stores]: isCustomer,
    [pageRoute.StoreDetails]: isCustomer,
    [pageRoute.Blogs]: isCustomer,
    [pageRoute.BlogDetails]: isCustomer,
    [pageRoute.Newsletters]: isCustomer,
    [pageRoute.NewsletterEdit]: isCustomer,
    [pageRoute.Campaigns]: isCustomer,
    [pageRoute.CampaignDetails]: isCustomer,
    [pageRoute.CampaignStepEdit]: isCustomer,
    [pageRoute.EmailStatistics]: isCustomer,
    [pageRoute.SmsTemplates]: isCustomer,
    [pageRoute.SmsTemplateDetails]: isCustomer,
    [pageRoute.SmsStatistics]: isCustomer,
    [pageRoute.SmsStatisticsDetails]: isCustomer,
    [pageRoute.AutomationRules]: isCustomer,
    [pageRoute.AutomationRulesCreate]: isCustomer,
    [pageRoute.AutomationRuleDetails]: isCustomer,
    [pageRoute.Workflows]: isCustomer,
    [pageRoute.WorkflowEmailDetails]: isCustomer,
    [pageRoute.WorkflowEmailWithRecipientDetails]: isCustomer,
    [pageRoute.WorkflowSmsDetails]: isCustomer,
    [pageRoute.Products]: isCustomer,
    [pageRoute.ProductsCreate]: isCustomer,
    [pageRoute.ProductDetails]: isCustomer,
    [pageRoute.Coupons]: isCustomer,
    [pageRoute.CouponDetails]: isCustomer,
    [pageRoute.Courses]: isCustomer,
    [pageRoute.CoursesAssignments]: isCustomer,
    [pageRoute.CoursesBundles]: isCustomer,
    [pageRoute.CoursesStudents]: isCustomer,
    [pageRoute.CourseBundleDetails]: isCustomer,
    [pageRoute.CourseDetails]: isCustomer,
    [pageRoute.CourseCurriculum]: isCustomer,
    [pageRoute.CourseCurriculumAssignmentCreate]: isCustomer,
    [pageRoute.CourseCurriculumAssignmentEdit]: isCustomer,
    [pageRoute.CourseCurriculumEmail]: isCustomer,
    [pageRoute.CourseLightCurriculum]: isCustomer,
    [pageRoute.CourseLightCurriculumLecture]: isCustomer,
    [pageRoute.CourseLightSettings]: isCustomer,
    [pageRoute.CourseQuizDetails]: isCustomer,
    [pageRoute.CourseQuizResults]: isCustomer,
    [pageRoute.CourseQuizStudentResults]: isCustomer,
    [pageRoute.CoursesStudentDetails]: isCustomer,
    [pageRoute.Communities]: isCustomer,
    [pageRoute.Files]: isCustomer,
    [pageRoute.Orders]: isCustomer,
    [pageRoute.Transactions]: isCustomer,
    [pageRoute.Subscriptions]: isCustomer,
    [pageRoute.AffiliateInvoices]: isCustomer && !isWsAssistant && isSIO(),
    [pageRoute.AffiliateProgram]: isCustomer && isWorkspaceOwner && isSIO(),
    [pageRoute.AffiliateProgramDetails]: isCustomer && isSIO(),
    [pageRoute.AdminUsers]: isSupportOrAdmin,
    [pageRoute.AdminEmailTemplates]: adminTemplatesPermission,
    [pageRoute.AdminPageTemplates]: adminTemplatesPermission,
    [pageRoute.AdminBlogTemplates]: adminTemplatesPermission,
    [pageRoute.AdminWebsiteTemplates]: adminTemplatesPermission,
    [pageRoute.AdminFunnelTemplates]: adminTemplatesPermission,
    [pageRoute.AdminCustomers]: adminCustomersPermission,
    [pageRoute.AdminEmailStats]: isSupportOrAdmin,
    [pageRoute.AdminEmailPendingMailing]: isSupportOrAdmin,
    [pageRoute.ManagementEmailPendingMailingDetails]: isSupportOrAdmin,
    [pageRoute.ManagementEmailStatsMailingDetails]: isSupportOrAdmin,
    [pageRoute.ManagementEmailStatsUserDetails]: isSupportOrAdmin,
    [pageRoute.AdminUserPlans]: !isWsAssistant && isAdmin,
    [pageRoute.AdminDomainsBlocked]: isSupportOrAdmin,
    [pageRoute.AdminDomainsWhitelist]: isSupportOrAdmin,
    [pageRoute.AdminSuspiciousPageKeywords]: isSupportOrAdmin,
    [pageRoute.AdminSuspiciousEmailKeywords]: isSupportOrAdmin,
    [pageRoute.AdminSuspiciousEmailAddress]: isSupportOrAdmin,
    [pageRoute.AdminStatistics]: (isWorkspaceOwner || isWsAdmin || isWsAssistant) && isRootAdmin,
    [pageRoute.ProfileUserSettings]: (isCustomer || isAffiliate) && isWorkspaceOwner,
    [pageRoute.ProfileAccountSettings]: isCustomer || isStudent,
    [pageRoute.ProfileUserPlan]: isCustomer && isWorkspaceOwner,
    [pageRoute.ProfilePaymentGateways]: isCustomer && isWorkspaceOwner,
    [pageRoute.ProfilePaymentGatewaysPaypal]: isCustomer && isWorkspaceOwner,
    [pageRoute.ProfileManageSubscriptions]: (isCustomer || isStudent) && isWorkspaceOwner,
    [pageRoute.ProfileMailingSettings]: isCustomer,
    [pageRoute.ProfileCourseSettings]: isCustomer,
    [pageRoute.ProfilePaymentSettings]: isCustomer && (isWorkspaceOwner || isWsAdmin),
    [pageRoute.ProfileSalesFunnelsSettings]: isCustomer,
    [pageRoute.ProfileShippingSettings]: isCustomer && isWorkspaceOwner,
    [pageRoute.ProfileAffiliateProgramSettings]: isCustomer && isWorkspaceOwner && isSIO(),
    [pageRoute.ProfileCustomDomainsSettings]: isCustomer && isWorkspaceOwner,
    [pageRoute.ProfilePublicApiSettings]: isCustomer && (isWorkspaceOwner || isWsAdmin) && isSIO(),
    [pageRoute.ProfileWebhookSettings]: isCustomer && (isWorkspaceOwner || isWsAdmin),
    [pageRoute.ProfileWebhookSettingsDetails]: isCustomer && (isWorkspaceOwner || isWsAdmin),
    [pageRoute.ProfileIntegrations]: isCustomer && (isWorkspaceOwner || isWsAdmin),
    [pageRoute.ProfileAffiliateSettings]:
      (isCustomer || isAffiliate) && isWorkspaceOwner && isSIO(),
    [pageRoute.ProfileNotificationSettings]: true,
    [pageRoute.ProfileWorkspaceMembers]: isCustomer && (isWorkspaceOwner || isWsAdmin),
    [pageRoute.ProfileChangePassword]: changePasswordPermission,
  }

  return {
    isReady,
    pagePermissions,
  }
}
